/**
 * The shell's Client switcher rows (task 276), from the caller's tenants and
 * their part of the organization tree, in the shape `@bluesphere/shell`
 * 0.3.0 draws: `kind` for the marker, `indent` for the guides, `group` for a
 * header row. The rules are the ones MDR's own `ClientSwitcher` (task 201,
 * task 208) kept, now as data:
 *
 *   - tree mode (the caller has a home): the visible tree in order, the
 *     caller's home left out (choosing it is choosing nothing, "All
 *     Clients"), each MSP selectable as "All of ‹MSP›" (`org:` value), and a
 *     Client only once connected;
 *   - flat mode (no tree yet): the connected Clients by the server's order;
 *   - the platform-operations tenant (task 200) under its own heading, last.
 *
 * The selection on the wire is a Client's id or `org:<id>`; the hook keeps
 * them apart.
 */

import type { ClientTenant, OrgNode } from '@bluesphere/api-client';
import type { ShellClient } from '@bluesphere/shell';

import { isInService } from '../lib/clientStatus';
import { depthOf, isParentKind, orgLabel, pathTo, type OrgIndex } from '../lib/orgs';

export const ORG = 'org:';
export const OPERATIONS_GROUP = 'BlueSphere operations';

export interface ClientSelection {
  tenantId: string | null;
  orgId: string | null;
}

/** The switcher's value for a selection. */
export function selectionValue({ tenantId, orgId }: ClientSelection): string | null {
  if (tenantId) return tenantId;
  if (orgId) return `${ORG}${orgId}`;
  return null;
}

/** A switcher value back to a selection. */
export function parseSelection(value: string | null): ClientSelection {
  if (!value) return { tenantId: null, orgId: null };
  if (value.startsWith(ORG)) return { tenantId: null, orgId: value.slice(ORG.length) };
  return { tenantId: value, orgId: null };
}

function where(orgs: OrgIndex, id: string): string | undefined {
  const above = pathTo(orgs, id).slice(0, -1);
  return above.length > 0 ? above.map((p) => orgLabel(p)).join(' › ') : undefined;
}

export function clientOptions(tenants: ClientTenant[], orgs: OrgIndex): ShellClient[] {
  const consented = tenants.filter(isInService);
  const consentedIds = new Set(consented.map((t) => t.client_tenant_id));
  const operationsRows = (rows: ShellClient[]) =>
    rows.map((r) => ({ ...r, group: OPERATIONS_GROUP }));

  if (orgs.homeId === null) {
    // Flat mode: as before the tree existed.
    const clients = consented
      .filter((t) => t.kind !== 'operations')
      .map((t) => ({ id: t.client_tenant_id, label: t.display_name }));
    const operations = consented
      .filter((t) => t.kind === 'operations')
      .map((t) => ({ id: t.client_tenant_id, label: t.display_name }));
    return [...clients, ...operationsRows(operations)];
  }

  const homeDepth = depthOf(orgs, orgs.homeId);
  const tree: ShellClient[] = [];
  const operations: ShellClient[] = [];
  for (const n of orgs.ordered) {
    if (n.org_id === orgs.homeId) continue;
    const indent = Math.max(0, depthOf(orgs, n.org_id) - homeDepth - 1);
    if (isParentKind(n)) {
      tree.push({
        id: `${ORG}${n.org_id}`,
        label: `All of ${orgLabel(n)}`,
        pill: `All of ${orgLabel(n)}`,
        kind: 'msp',
        indent,
        detail: where(orgs, n.org_id),
      });
    } else if (n.kind === 'client' && consentedIds.has(n.org_id)) {
      tree.push({
        id: n.org_id,
        label: orgLabel(n),
        kind: 'client',
        indent,
        detail: where(orgs, n.org_id),
      });
    } else if (n.kind === 'operations' && consentedIds.has(n.org_id)) {
      operations.push({ id: n.org_id, label: orgLabel(n), kind: 'client' });
    }
  }
  return [...tree, ...operationsRows(operations)];
}
